/**
 * <EmployeesArchiveDialog> — confirm archive of an employee.
 *
 * Phase-2 Wave-4 W4.T3. AlertDialog (Radix) — destructive confirm with
 * a 460px content surface. Two display modes:
 *
 *   1. **Confirm mode** (default): «Архивировать <ФИО>?» + cascade copy
 *      describing the irreversibility.
 *   2. **Error mode**: replaces the body when archive returns 422
 *      `last_admin` / `last_admin_gip`. Only «Закрыть» is shown — there is
 *      no retry path; the actor must first appoint another admin.
 *
 *      ⚠️ КОД `last_manager_in_department` ОТСЮДА УБРАН ПО РЕШЕНИЮ Р-109
 *      «Гейт архивации последнего руководителя отдела отпускается»
 *      (31.08.2026), и возвращать его сюда НЕ НАДО — причин две, каждой
 *      хватило бы отдельно:
 *        — эндпоинт архивации этот код НЕ ОТДАЁТ и не отдавал никогда:
 *          он рождается только в `PATCH /users/:id` и в отображении
 *          триггера `user_role_transition_guard` (миграция 006), а тот
 *          висит на `BEFORE UPDATE OF "roleId"` — архивация `roleId` не
 *          трогает. Живой этот код на пути PATCH, и там его обрабатывает
 *          `employees-detail.modal.tsx`, которого волна не касалась;
 *        — его текст советовал «сначала назначьте нового РО для отдела»,
 *          то есть ровно тот шаг, который триггер
 *          `user_one_active_ro_per_dept` (миграция 002) делает
 *          НЕВОЗМОЖНЫМ. Мёртвая ветка — полбеды; мёртвая ветка с ложным
 *          советом — та самая, ради которой Р-109 и записано.
 *
 *      ⚠️ `last_admin_gip` для архивации ТОЖЕ недостижим (тот же разбор),
 *      но он оставлен НАМЕРЕННО: сам гейт — предмет другого решения, и
 *      волна по Р-109 его не трогает. Асимметрия сознательная, а не
 *      недосмотр.
 *
 *      ⚠️ ПОПРАВКА ФИКС-ПАКА КРУГА (31.08.2026): довод выше опирался на
 *      совет «передайте проекты другому ГИПу» — но это текст СЕРВЕРА
 *      (`users.service.ts`, код `last_admin_gip`), а `lastGuardCopy`
 *      печатала ЗДЕСЬ совсем другой: «Это последний активный ГИП.
 *      Сначала назначьте другого ГИП». Печатаемый текст был ложен по
 *      условию — гейт срабатывает, когда за человеком ЧИСЛЯТСЯ ПРОЕКТЫ, а
 *      не когда он последний ГИП, — и совет «назначьте другого ГИПа»
 *      ничего не отпирал: проекты остаются за прежним. Комментарий и
 *      печатаемый текст разошлись, и прав был комментарий, поэтому текст
 *      приведён к серверному, а не наоборот. Ветка сохранена (снимать её
 *      значило бы решать за то, другое решение).
 *
 * Wired via `useArchiveUserMutation` from `@/api/users`. The mutation
 * already invalidates `qk.users.all` on success — the calling list
 * refetches without manual wiring.
 *
 * 409 ALREADY_ARCHIVED: silent (research-A §2.3 + research-B §6.2). The
 * row is already archived from the server's perspective, so we just
 * close the dialog and toast a success — refetch will reflect reality.
 */
import * as React from 'react';

import type { UserDetail } from '@aris/shared';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/toast';
import { ApiError } from '@/lib/fetcher';

import { useArchiveUserMutation } from '@/api/users';

const LAST_GUARD_CODES = new Set(['last_admin', 'last_admin_gip'] as const);

/**
 * Pure helper exported for tests — maps a guard code into a user-facing
 * Russian copy block. Falls back to a generic message for unknown codes.
 */
export function lastGuardCopy(code: string): {
  title: string;
  body: string;
} {
  if (code === 'last_admin') {
    return {
      title: 'Невозможно архивировать',
      body: 'Это последний активный администратор. Сначала назначьте другого администратора, затем повторите действие.',
    };
  }
  if (code === 'last_admin_gip') {
    return {
      title: 'Невозможно архивировать',
      // Текст СЛЕДУЕТ серверному (`users.service.ts`, код `last_admin_gip`):
      // мешают не «последний ГИП», а числящиеся за человеком проекты, и
      // отпирает их только передача другому ГИПу. Разбор — в шапке файла.
      body: 'Пользователь ведёт проекты как ГИП. Сначала передайте его проекты другому ГИПу, затем повторите действие.',
    };
  }
  return {
    title: 'Невозможно архивировать',
    body: 'Действие отклонено сервером. Обновите страницу и повторите попытку.',
  };
}

export interface EmployeesArchiveDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  user: UserDetail | null;
}

export function EmployeesArchiveDialog(
  props: EmployeesArchiveDialogProps,
): React.ReactElement | null {
  const { open, onOpenChange, user } = props;
  const archiveMutation = useArchiveUserMutation();

  const [errorCode, setErrorCode] = React.useState<string | null>(null);

  // Reset error state when the dialog closes / target changes.
  React.useEffect(() => {
    if (!open) setErrorCode(null);
  }, [open]);
  React.useEffect(() => {
    setErrorCode(null);
  }, [user?.id]);

  const onConfirm = React.useCallback(async () => {
    if (!user) return;
    try {
      await archiveMutation.mutateAsync({ id: user.id });
      toast('Сотрудник архивирован', { variant: 'success' });
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409 && err.code === 'ALREADY_ARCHIVED') {
          toast('Сотрудник уже архивирован', { variant: 'info' });
          onOpenChange(false);
          return;
        }
        if (err.status === 422 && err.code && LAST_GUARD_CODES.has(err.code as never)) {
          setErrorCode(err.code);
          return;
        }
        toast(err.message, { variant: 'error' });
        return;
      }
      toast('Не удалось архивировать.', { variant: 'error' });
    }
  }, [archiveMutation, onOpenChange, user]);

  if (!user) return null;

  const inErrorMode = errorCode !== null;
  const copy = inErrorMode
    ? lastGuardCopy(errorCode)
    : {
        title: `Архивировать ${user.fullName}?`,
        // ⚠️ ЗДЕСЬ ОБЕЩАЛОСЬ УДАЛЕНИЕ БУДУЩИХ ПЛАНОВЫХ ЧАСОВ — каскад снят
        // волной Ф4 · В10-А2 (Р-25, Р-26), и обещание стало ложным. Текст
        // правится ТЕМ ЖЕ коммитом, что и механизм: иначе пользователь
        // подтверждает то, чего не случится.
        body: 'Это действие необратимо: сотрудник потеряет доступ, а его активные сессии будут прерваны. Часы и история сохранятся. Карточка останется доступной на вкладке «Архив».',
      };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="employees-archive-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle data-testid="employees-archive-title">{copy.title}</AlertDialogTitle>
          <AlertDialogDescription data-testid="employees-archive-body">
            {copy.body}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {inErrorMode ? (
            <AlertDialogCancel
              data-testid="employees-archive-close"
              onClick={() => onOpenChange(false)}
            >
              Закрыть
            </AlertDialogCancel>
          ) : (
            <>
              <AlertDialogCancel data-testid="employees-archive-cancel">Отмена</AlertDialogCancel>
              <AlertDialogAction
                data-testid="employees-archive-confirm"
                onClick={(e) => {
                  // Prevent the AlertDialog from auto-closing — we close
                  // ourselves on success, or stay open on the 422 path.
                  e.preventDefault();
                  void onConfirm();
                }}
                disabled={archiveMutation.isPending}
                className="bg-destructive text-destructive-foreground hover:opacity-90"
              >
                Архивировать
              </AlertDialogAction>
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
